import React from 'react';
import PropTypes from 'prop-types';
import { Field, FieldArray } from 'redux-form';
import { capitalizeStringWithUnderscores } from 'utils/helperMethods';
import { transactionTypeNameMap } from 'utils/constants/commonValues';
import { FormElems } from '..';

// Card slabs
const renderSurchargeBankSlab = ({
    fields,
    meta: { error, submitFailed },
    sectionData,
    isViewMode,
    sectionName,
    walletOptions,
    bankOptions,
}) => {
    return (
        <>
            {fields.map((item, index) => (
                <ul key={index} className="slabCard-container border-bottom">
                    <FieldArray
                        name={`${item}.bankSlabs`}
                        component={renderBankSlabItems}
                        bankOptions={bankOptions}
                        props={{ sectionData, isViewMode }}
                        sectionName={sectionName}
                    />
                </ul>
            ))}
        </>
    );
};

// card slabs
const renderBankSlabItems = ({ fields, meta: { error, submitFailed }, sectionData, isViewMode, sectionName }) => {
    const section = fields.getAll();

    // if lower slab <=2000 and higherslab is null
    const noSlab = section.find(item => {
        return (
            item.lowerSlab !== null && item.lowerSlab < 2000 && !item.higherSlab
        );
    });

    const cardTitle = (title, sectionName, accountType, transactionType, category, cardType) => {
        let cardTitle = title;
        if (title === 'ESF') {
            cardTitle = 'SAME DAY SETTLEMENT FEE';
        }

        if (sectionName === 'UPI' && accountType) {
            cardTitle = `${cardTitle} for ${capitalizeStringWithUnderscores(accountType)}`;
        }

        if (transactionType) {
            cardTitle = `${cardTitle} (${transactionTypeNameMap[transactionType]})`;
        }

        if (category) {
            cardTitle = `${cardTitle} (${category})`;
        }

        if (cardType) {
            cardTitle = `${cardTitle} - ${cardType} `;
        }

        return cardTitle?.replaceAll('_', ' ');
    };

    const tiles = (item, index) => {
        return (
            <li className={`cardTile ${section[index].transactionType === 'INSTANT_REFUND' ? 'negative-fee' : ''}`}>
                <div className="slab-title">
                    <div className="title">
                        {`${cardTitle(
                            section[index]?.type,
                            sectionName,
                            section[index].accountType,
                            section[index].transactionType,
                            section[index].category,
                            section[index].cardType
                        )}`}
                    </div>
                </div>
                <div className="form-section">
                    <div className="form-field">
                        <p className="label">Percent</p>
                        <Field
                            name={`${item}.feePercent`}
                            id={`${item}-feePercent`}
                            type="text"
                            component={FormElems.Input}
                            placeholder="%"
                            postfixContent="%"
                            disabled={isViewMode}
                            borderRadius="5px"
                            maxDecimals={4}
                        />
                    </div>
                    <div className="form-field">
                        <p className="label">Flat</p>
                        <Field
                            name={`${item}.feeFlat`}
                            id={`${item}-feeFlat`}
                            type="text"
                            component={FormElems.Input}
                            placeholder="INR"
                            disabled={isViewMode}
                            maxDecimals={2}
                        />
                    </div>
                    <div className="form-field">
                        <p className="label">Criteria</p>
                        <Field
                            name={`${item}.feeCriteria`}
                            id={`${item}-feeCriteria`}
                            data={[
                                {
                                    label: 'Minimum',
                                    value: 'MINIMUM',
                                },
                                {
                                    label: 'Maximum',
                                    value: 'MAXIMUM',
                                },
                                {
                                    label: 'Both',
                                    value: 'BOTH',
                                },
                            ]}
                            labelField="label"
                            valueField="value"
                            component={FormElems.Select}
                            label="Criteria"
                            disabled={isViewMode}
                        />
                    </div>
                    <div className="form-field">
                        <p className="label">Tax</p>
                        <Field
                            name={`${item}.tax`}
                            id={`${item}-tax`}
                            type="text"
                            component={FormElems.Input}
                            placeholder="%"
                            postfixContent="%"
                            disabled={isViewMode}
                            borderRadius="5px"
                            maxDecimals={2}
                        />
                    </div>
                </div>
            </li>
        );
    };

    const renderSlabs = () => {
        // get left slabs
        const leftSlab = fields.map((item, index) => {
            if (
                section[index].higherSlab &&
                section[index].higherSlab <= 2000
            ) {
                return tiles(item, index);
            }
        });

        // get right slabs
        const rightSlab = fields.map((item, index) => {
            if (section[index].lowerSlab && section[index].lowerSlab >= 2000) {
                return tiles(item, index);
            }
        });
        
        // Default slab (less than 100)
        const defaultSlab = fields
            .map((item, index) => {
                if (!section[index].higherSlab && section[index].lowerSlab <= 100) {
                    return tiles(item, index);
                }
                return null;
            })
            .filter(Boolean);

        // 0-250 slab
        const zeroTo250Slab = fields
            .map((item, index) => {
                if (
                    section[index].lowerSlab === 0 &&
                    section[index].higherSlab === 250 &&
                    (section[index].transactionType === 'MNDT_REG_PAY' ||
                        section[index].transactionType === 'MNDT_EXE_PAY')
                ) {
                    return tiles(item, index);
                }
                return null;
            })
            .filter(Boolean);

        // 250-1000 slab (for MNDT_EXE_PAY)
        const slab250To1000 = fields
            .map((item, index) => {
                if (
                    section[index].lowerSlab === 250 &&
                    section[index].higherSlab === 1000 &&
                    section[index].transactionType === 'MNDT_EXE_PAY'
                ) {
                    return tiles(item, index);
                }
                return null;
            })
            .filter(Boolean);

        // 250-500 slab (for MNDT_REG_PAY)
        const slab250To500 = fields
            .map((item, index) => {
                if (
                    section[index].lowerSlab === 250 &&
                    section[index].higherSlab === 500 &&
                    section[index].transactionType === 'MNDT_REG_PAY'
                ) {
                    return tiles(item, index);
                }
                return null;
            })
            .filter(Boolean);

        // 1000+ slab (for MNDT_EXE_PAY)
        const slab1000Plus = fields
            .map((item, index) => {
                if (
                    section[index].lowerSlab === 1000 &&
                    !section[index].higherSlab &&
                    section[index].transactionType === 'MNDT_EXE_PAY'
                ) {
                    return tiles(item, index);
                }
                return null;
            })
            .filter(Boolean);

        // 500+ slab (for MNDT_REG_PAY)
        const slab500Plus = fields
            .map((item, index) => {
                if (
                    section[index].lowerSlab === 500 &&
                    !section[index].higherSlab &&
                    section[index].transactionType === 'MNDT_REG_PAY'
                ) {
                    return tiles(item, index);
                }
                return null;
            })
            .filter(Boolean);

        // Regular transaction slabs - upto 100
        const firstSlab = fields
            .map((item, index) => {
                if (
                    section[index].higherSlab &&
                    section[index].higherSlab <= 100 &&
                    section[index].transactionType !== 'MNDT_REG_PAY' &&
                    section[index].transactionType !== 'MNDT_EXE_PAY'
                ) {
                    return tiles(item, index);
                }
                return null;
            })
            .filter(Boolean);

        return (
            <>
                    <ul className="leftSlab-container slabCard-container slab">
                        <div className="slab-heading">Slab - Upto 2000</div>
                        {leftSlab}
                    </ul>
                    <ul className="rightSlab-container slabCard-container slab">
                        <p className="slab-heading">Slab - Above 2000 </p>
                        {rightSlab}
                    </ul>

                {zeroTo250Slab.length > 0 && (
                    <ul className="leftSlab-container slabCard-container slab">
                        <div className="slab-heading">Slab - 0 to 250</div>
                        {zeroTo250Slab}
                    </ul>
                )}

                {slab250To1000.length > 0 && (
                    <ul className="rightSlab-container slabCard-container slab">
                        <p className="slab-heading">Slab - 250 to 1000</p>
                        {slab250To1000}
                    </ul>
                )}

                {slab250To500.length > 0 && (
                    <ul className="rightSlab-container slabCard-container slab">
                        <p className="slab-heading">Slab - 250 to 500</p>
                        {slab250To500}
                    </ul>
                )}

                {slab1000Plus.length > 0 && (
                    <ul className="leftSlab-container slabCard-container slab">
                        <div className="slab-heading">Slab - Above 1000</div>
                        {slab1000Plus}
                    </ul>
                )}

                {slab500Plus.length > 0 && (
                    <ul className="leftSlab-container slabCard-container slab">
                        <div className="slab-heading">Slab - Above 500</div>
                        {slab500Plus}
                    </ul>
                )}

                {firstSlab.length > 0 && (
                    <ul className="leftSlab-container slabCard-container slab">
                        <div className="slab-heading">Slab - Upto 100</div>
                        {firstSlab}
                    </ul>
                )}

                {defaultSlab.length > 0 && (
                    <ul className="leftSlab-container slabCard-container slab">
                        <div className="slab-heading">Payment Slabs</div>
                        {defaultSlab}
                    </ul>
                )}
            </>
        );
    };

    if (noSlab) {
        return fields.map((item, index) => {
            return tiles(item, index);
        });
    }

    return (
        <div style={{ display: 'flex', flexWrap: 'wrap' }} className="upi-slabs">
            {renderSlabs()}
        </div>
    );
};

renderSurchargeBankSlab.propTypes = {
    fields: PropTypes.object.isRequired,
    meta: PropTypes.object,
    sectionData: PropTypes.object,
    bankOptions: PropTypes.array,
    isViewMode: PropTypes.bool,
    sectionName: PropTypes.string,
    walletOptions: PropTypes.array,
};

renderBankSlabItems.propTypes = {
    fields: PropTypes.object.isRequired,
    meta: PropTypes.object,
    sectionData: PropTypes.object,
    bankOptions: PropTypes.array,
    isViewMode: PropTypes.bool,
    sectionName: PropTypes.string,
};

export default renderSurchargeBankSlab;